import React, { useState, useEffect } from 'react';
import { Select, Form } from 'antd';
import type { ComponentConfig, ValueChangeEvent, DropdownOption } from '../types';
import { getDropdownOptions } from '../types';
import { dropdownCache } from '../utils/dropdownCache';
import { useStore, useSelectedNodeId } from '../../../store/store';
import { usePropertyValues } from '../PropertyValueContext';
import { createContextLogger } from '../../../utils/logger';

const logger = createContextLogger('Dropdown');

const { Option } = Select;

interface DropdownProps {
  config: ComponentConfig;
  value?: any;
  onChange: (event: ValueChangeEvent) => void;
  pageId: string;
  error?: string[];
}

export const Dropdown: React.FC<DropdownProps> = ({
  config,
  value,
  onChange,
  pageId,
  error
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [dynamicOptions, setDynamicOptions] = useState<DropdownOption[]>([]);
  
  // Get stable selected node ID
  const { nodeId } = useSelectedNodeId();
  
  // Get access to other component values for cross-field validation
  const { getValue, values } = usePropertyValues();
  
  // Get pipeline and transform context for cache scoping
  const { activePipelinePath, selectedNode, isWorkflow } = useStore((state) => {
    const activePipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
    const selectedNode = activePipeline?.nodes.find((n) => n.id === nodeId);
    return {
      activePipelinePath: activePipeline?.id, // Pipeline ID is actually the file path
      selectedNode,
      isWorkflow: activePipeline?.type === 'workflow'
    };
  });
  
  // Extract dropdown specific options from additionalOptions
  const dropdownOptions = getDropdownOptions(config);
  


  // Clear options when context changes
  useEffect(() => {
    setDynamicOptions([]);
    setLoading(false);
  }, [activePipelinePath, selectedNode?.data?.type]);

  // Cross-field validation is now handled automatically by PropertyValueManager

  // Fetch dynamic options when component mounts or dependencies change
  useEffect(() => {
    if (dropdownOptions.dynamicLoad && dropdownOptions.transformType && dropdownOptions.componentId && activePipelinePath && selectedNode?.id) {
      fetchDynamicOptions();
    }
  }, [
    dropdownOptions.dynamicLoad,
    dropdownOptions.transformType,
    dropdownOptions.componentId,
    config.id,
    activePipelinePath,
    selectedNode?.data?.type,
    selectedNode?.id
  ]);

  const fetchDynamicOptions = async () => {
    if (!dropdownOptions.transformType || !dropdownOptions.componentId || !activePipelinePath || !selectedNode?.id) {
      logger.warn('Dynamic dropdown missing required parameters:', {
        transformType: dropdownOptions.transformType,
        componentId: dropdownOptions.componentId,
        pipelinePath: activePipelinePath,
        transformName: selectedNode?.id
      });
      return;
    }
    
    const transformName = selectedNode.id;
    
    setLoading(true);
    try {
      logger.debug('Fetching dynamic dropdown options via cache:', {
        transformType: dropdownOptions.transformType,
        componentId: dropdownOptions.componentId,
        pipelinePath: activePipelinePath,
        transformName: transformName
      });
      
      const options = await dropdownCache.getOptions({
        pipelinePath: activePipelinePath,
        transformType: dropdownOptions.transformType,
        componentId: dropdownOptions.componentId,
        transformName: transformName,
        isWorkflow: isWorkflow
      });
      
      setDynamicOptions(options);
      logger.debug('Dynamic dropdown options loaded via cache:', {
        transformType: dropdownOptions.transformType,
        componentId: dropdownOptions.componentId,
        pipelinePath: activePipelinePath,
        transformName: transformName,
        count: options.length
      });
    } catch (error) {
      logger.error('Failed to fetch dynamic dropdown options:', error);
      setDynamicOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (selectedValue: any) => {
    onChange({
      componentId: config.id,
      value: selectedValue,
      pageId
    });
  };

  const validateRules = () => {
    const rules: any[] = [];
    
    if (config.required) {
      rules.push({
        required: true,
        message: `${config.label} is required`
      });
    }
    
    // Cross-field validation is now handled in PropertyValueManager
    
    // Add custom validation rules
    config.validation?.forEach(rule => {
      switch (rule.type) {
        case 'custom':
          rules.push({
            validator: rule.value,
            message: rule.message
          });
          break;
        // Add more validation types as needed
      }
    });
    
    return rules;
  };

  const getCurrentValue = () => {
    return value !== undefined ? value : config.defaultValue;
  };

  // Use dynamic options if available, fallback to static options
  const optionsToRender = dropdownOptions.dynamicLoad 
    ? dynamicOptions 
    : dropdownOptions.options || [];

  const selectProps = {
    value: getCurrentValue(),
    onChange: handleChange,
    disabled: config.disabled || loading,
    mode: dropdownOptions.multiple ? ('multiple' as const) : undefined,
    allowClear: dropdownOptions.allowClear !== false,
    showSearch: dropdownOptions.searchable !== false,
    placeholder: loading ? 'Loading options...' : `Select ${config.label.toLowerCase()}`,
    style: { width: '100%' },
    loading: loading,
    filterOption: dropdownOptions.searchable !== false ? (input: string, option: any) => {
      return option?.children?.toLowerCase().includes(input.toLowerCase()) ||
             option?.value?.toLowerCase().includes(input.toLowerCase());
    } : false
  };

  const formItemProps = {
    label: config.label,
    required: config.required,
    validateStatus: error && error.length > 0 ? 'error' as const : undefined,
    help: error && error.length > 0 ? error[0] : config.description,
    rules: validateRules(),
    labelCol: { span: 24 },
    wrapperCol: { span: 24 }
  };

  return (
    <Form.Item {...formItemProps}>
      <Select {...selectProps}>
        {optionsToRender.map((option) => (
          <Option 
            key={option.value} 
            value={option.value}
            disabled={option.disabled}
            title={option.description}
          >
            {option.label}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
}; 